// src/components/ProtectedRoute/Admin/Dialogs/BionomicRecordEditor.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog, // ← Distribution サブダイアログ用だけで使う（BaseDialogとは別）
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Button,
  MenuItem,
  TextField,
  Typography,
  Stack,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  IconButton,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import BaseDialog from "./Framework/BaseDialog";
import PublicationSelector from "./parts/PublicationSelector";
import ScientificNameSelector from "./parts/ScientificNameSelector";
import supabase from "../../../../utils/supabase";
import { useMasterData } from "../../../../utils/MasterDataProvider";

/* =========================================================
   Distribution サブダイアログ（個別：BaseDialogは使わない）
   ========================================================= */
function dmsToDecimal({ deg, min, sec, dir }) {
  const d = parseFloat(deg) || 0;
  const m = parseFloat(min) || 0;
  const s = parseFloat(sec) || 0;
  if ([d, m, s].some((v) => Number.isNaN(v))) return null;
  let decimal = Math.abs(d) + m / 60 + s / 3600;
  if (dir === "S" || dir === "W") decimal = -decimal;
  return decimal;
}

function DistributionEditorDialog({
  open,
  onClose,
  countries = [],
  initialValue = null, // {country,state,city,detail,latitude,longitude} | null
}) {
  const [latFormat, setLatFormat] = useState("decimal");
  const [lonFormat, setLonFormat] = useState("decimal");
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [detail, setDetail] = useState("");
  const [latDecimal, setLatDecimal] = useState("");
  const [lonDecimal, setLonDecimal] = useState("");
  const [latDMS, setLatDMS] = useState({ deg: "", min: "", sec: "", dir: "N" });
  const [lonDMS, setLonDMS] = useState({ deg: "", min: "", sec: "", dir: "E" });

  useEffect(() => {
    if (!open) return;
    const v = initialValue || {};
    setCountry(v.country || "");
    setState(v.state || "");
    setCity(v.city || "");
    setDetail(v.detail || "");
    setLatFormat("decimal");
    setLonFormat("decimal");
    setLatDecimal(v.latitude != null ? String(v.latitude) : "");
    setLonDecimal(v.longitude != null ? String(v.longitude) : "");
    setLatDMS({ deg: "", min: "", sec: "", dir: "N" });
    setLonDMS({ deg: "", min: "", sec: "", dir: "E" });
  }, [open, initialValue]);

  const handleSave = () => {
    const finalLat =
      latFormat === "decimal" ? (latDecimal === "" ? null : parseFloat(latDecimal)) : dmsToDecimal(latDMS);
    const finalLon =
      lonFormat === "decimal" ? (lonDecimal === "" ? null : parseFloat(lonDecimal)) : dmsToDecimal(lonDMS);

    onClose(true, {
      country: (country || "").trim(),
      state: (state || "").trim(),
      city: (city || "").trim(),
      detail: (detail || "").trim(),
      latitude: Number.isNaN(finalLat) ? null : finalLat,
      longitude: Number.isNaN(finalLon) ? null : finalLon,
    });
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>{initialValue ? "Edit Distribution" : "Add Distribution"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Autocomplete
            options={countries}
            getOptionLabel={(c) => c?.id || ""}
            value={countries.find((cn) => cn.id === country) || null}
            onChange={(_, v) => setCountry(v ? v.id : "")}
            renderInput={(params) => <TextField {...params} label="Country" variant="outlined" />}
          />
          <TextField label="State/Prefecture" value={state} onChange={(e) => setState(e.target.value)} />
          <TextField label="City" value={city} onChange={(e) => setCity(e.target.value)} />
          <TextField label="Detail Locality" value={detail} onChange={(e) => setDetail(e.target.value)} />

          {/* Latitude */}
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              select
              label="Lat Format"
              value={latFormat}
              onChange={(e) => setLatFormat(e.target.value)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="decimal">Decimal</MenuItem>
              <MenuItem value="dms">DMS</MenuItem>
            </TextField>
            {latFormat === "decimal" ? (
              <TextField
                label="Latitude (decimal)"
                type="number"
                step="any"
                value={latDecimal}
                onChange={(e) => setLatDecimal(e.target.value)}
                fullWidth
              />
            ) : (
              <Stack direction="row" spacing={1} sx={{ flexGrow: 1 }}>
                <TextField
                  label="Deg"
                  type="number"
                  value={latDMS.deg}
                  onChange={(e) => setLatDMS({ ...latDMS, deg: e.target.value })}
                  sx={{ width: "20%" }}
                />
                <TextField
                  label="Min"
                  type="number"
                  value={latDMS.min}
                  onChange={(e) => setLatDMS({ ...latDMS, min: e.target.value })}
                  sx={{ width: "20%" }}
                />
                <TextField
                  label="Sec"
                  type="number"
                  step="any"
                  value={latDMS.sec}
                  onChange={(e) => setLatDMS({ ...latDMS, sec: e.target.value })}
                  sx={{ width: "20%" }}
                />
                <TextField
                  select
                  label="Dir"
                  value={latDMS.dir}
                  onChange={(e) => setLatDMS({ ...latDMS, dir: e.target.value })}
                  sx={{ minWidth: 70 }}
                >
                  <MenuItem value="N">N</MenuItem>
                  <MenuItem value="S">S</MenuItem>
                </TextField>
              </Stack>
            )}
          </Stack>

          {/* Longitude */}
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              select
              label="Lon Format"
              value={lonFormat}
              onChange={(e) => setLonFormat(e.target.value)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="decimal">Decimal</MenuItem>
              <MenuItem value="dms">DMS</MenuItem>
            </TextField>
            {lonFormat === "decimal" ? (
              <TextField
                label="Longitude (decimal)"
                type="number"
                step="any"
                value={lonDecimal}
                onChange={(e) => setLonDecimal(e.target.value)}
                fullWidth
              />
            ) : (
              <Stack direction="row" spacing={1} sx={{ flexGrow: 1 }}>
                <TextField
                  label="Deg"
                  type="number"
                  value={lonDMS.deg}
                  onChange={(e) => setLonDMS({ ...lonDMS, deg: e.target.value })}
                  sx={{ width: "20%" }}
                />
                <TextField
                  label="Min"
                  type="number"
                  value={lonDMS.min}
                  onChange={(e) => setLonDMS({ ...lonDMS, min: e.target.value })}
                  sx={{ width: "20%" }}
                />
                <TextField
                  label="Sec"
                  type="number"
                  step="any"
                  value={lonDMS.sec}
                  onChange={(e) => setLonDMS({ ...lonDMS, sec: e.target.value })}
                  sx={{ width: "20%" }}
                />
                <TextField
                  select
                  label="Dir"
                  value={lonDMS.dir}
                  onChange={(e) => setLonDMS({ ...lonDMS, dir: e.target.value })}
                  sx={{ minWidth: 70 }}
                >
                  <MenuItem value="E">E</MenuItem>
                  <MenuItem value="W">W</MenuItem>
                </TextField>
              </Stack>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* =========================================================
   本体: Add / Edit を 1 本化した Editor（BaseDialog 版）
   ========================================================= */
const INITIAL_FORM = {
  id: null,
  source_publication_id: null, // ← ここは ID だけを保持
  page_start: "",
  page_end: "",
  target_taxa_id: "",
  data_type: "", // "distribution" | "ecology"
  ecological_tags: [], // [{id,name}]
  host_taxon_id: "",
  other_related_taxon_id: "",
  remark: "",
  distribution: [], // [{ country, state, city, detail, latitude, longitude }]
  data_origin_id: "",
  reliability_id: "",
  verification_status_id: "",
};

function toPayload(form) {
  const ecoTagIds = (form.ecological_tags || []).map((t) => t.id).filter(Boolean);
  const pageStart = form.page_start === "" ? null : parseInt(form.page_start, 10);
  const pageEnd = form.page_end === "" ? null : parseInt(form.page_end, 10);
  return {
    source_publication_id: form.source_publication_id ?? null,
    page_start: Number.isNaN(pageStart) ? null : pageStart,
    page_end: Number.isNaN(pageEnd) ? null : pageEnd,
    target_taxa_id: form.target_taxa_id?.trim() || null,
    data_type: form.data_type?.trim() || null,
    ecological_tags: ecoTagIds.length ? ecoTagIds : null,
    remark: form.remark?.trim() || null,
    host_taxon_id: form.host_taxon_id?.trim() || null,
    other_related_taxon_id: form.other_related_taxon_id?.trim() || null,
    distribution: form.distribution?.length ? form.distribution : null,
    data_origin_id: form.data_origin_id || null,
    reliability_id: form.reliability_id || null,
    verification_status_id: form.verification_status_id || null,
  };
}

function normalizeFormFromRecord(rec, ecoTagsList) {
  const ecoObjs = Array.isArray(rec?.ecological_tags)
    ? rec.ecological_tags
        .map((idOrObj) =>
          typeof idOrObj === "object" ? idOrObj : (ecoTagsList || []).find((et) => et.id === idOrObj)
        )
        .filter(Boolean)
    : [];

  return {
    ...INITIAL_FORM,
    id: rec?.id ?? null,
    source_publication_id: rec?.source_publication_id ?? null,
    page_start: rec?.page_start ?? "",
    page_end: rec?.page_end ?? "",
    target_taxa_id: rec?.target_taxa_id ?? "",
    data_type: rec?.data_type ?? "",
    ecological_tags: ecoObjs,
    host_taxon_id: rec?.host_taxon_id ?? "",
    other_related_taxon_id: rec?.other_related_taxon_id ?? "",
    remark: rec?.remark ?? "",
    distribution: Array.isArray(rec?.distribution) ? rec.distribution : [],
    data_origin_id: rec?.data_origin_id ?? "",
    reliability_id: rec?.reliability_id ?? "",
    verification_status_id: rec?.verification_status_id ?? "",
  };
}

// ScientificNameSelector 用：表示ラベルに必要な最小列を取得
async function loadScientificNamesForSelect() {
  const { data, error } = await supabase
    .from("scientific_names")
    .select("id, name_spell_valid, current_parent, current_rank")
    .order("id");
  if (error) return [];
  return data || [];
}

export default function BionomicRecordEditor({
  open,
  onClose,
  recordData = null, // 編集対象 (null なら追加モード)
  allPublications = null, // 親から供給可能なら [{id,title_english}] でもOK
}) {
  const isEdit = !!recordData?.id;
  const { countries: countriesMaster } = useMasterData();

  // マスター
  const [countries, setCountries] = useState([]);
  const [scientificNames, setScientificNames] = useState([]); // [{id,name_spell_valid,current_parent,current_rank}]
  const [ecologicalTags, setEcologicalTags] = useState([]); // [{id,name}]
  const [dataOrigins, setDataOrigins] = useState([]); // [{id,name}]
  const [reliabilities, setReliabilities] = useState([]); // [{id,name}]
  const [verificationStatuses, setVerificationStatuses] = useState([]); // [{id,name}]
  const [publications, setPublications] = useState(allPublications || []); // PublicationSelector に渡す種データ

  // フォーム
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Distribution sub
  const [distOpen, setDistOpen] = useState(false);
  const [distEditIndex, setDistEditIndex] = useState(null);

  // 変更有無の判定（BaseDialogの confirmCloseWhen に渡す）
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const isDirty = useMemo(() => {
    if (!initialSnapshot) return false;
    try {
      return JSON.stringify(form) !== initialSnapshot;
    } catch {
      return true;
    }
  }, [form, initialSnapshot]);

  const resetState = useCallback(() => {
    setCountries([]);
    setScientificNames([]);
    setEcologicalTags([]);
    setDataOrigins([]);
    setReliabilities([]);
    setVerificationStatuses([]);
    setPublications(allPublications || []);
    setForm(INITIAL_FORM);
    setSaving(false);
    setLoadingOptions(true);
    setDistOpen(false);
    setDistEditIndex(null);
    setInitialSnapshot(null);
  }, [allPublications]);

  // Publications 再取得（PublicationSelector の onRefreshData からも呼ぶ）
  const refreshPublications = useCallback(async () => {
    const { data } = await supabase
      .from("publications")
      .select("id, title_english, publication_date, volume, number, page, journal_id")
      .order("id");
    setPublications(data || []);
  }, []);

  // 初期ロード
  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    let mounted = true;
    setLoadingOptions(true);

    (async () => {
      try {
        const [sn, ecoRes, doRes, reRes, vsRes, pubsRes] = await Promise.all([
          loadScientificNamesForSelect(),
          supabase.from("ecological_tags").select("id, name").order("name", { ascending: true }),
          supabase.from("data_origins").select("id, name").order("id", { ascending: true }),
          supabase.from("data_reliabilities").select("id, name").order("id", { ascending: true }),
          supabase.from("verification_statuses").select("id, name").order("id", { ascending: true }),
          allPublications
            ? Promise.resolve({ data: allPublications, error: null })
            : supabase
                .from("publications")
                .select("id, title_english")
                .order("id"),
        ]);

        if (!mounted) return;
        setScientificNames(sn || []);
        setCountries(countriesMaster && countriesMaster.length ? countriesMaster : []);
        setEcologicalTags(ecoRes.data || []);
        setDataOrigins(doRes.data || []);
        setReliabilities(reRes.data || []);
        setVerificationStatuses(vsRes.data || []);
        setPublications(pubsRes.data || []);

        // フォーム初期化
        const next = isEdit ? normalizeFormFromRecord(recordData, ecoRes.data || []) : { ...INITIAL_FORM };
        setForm(next);
        setInitialSnapshot(JSON.stringify(next));
      } finally {
        if (mounted) setLoadingOptions(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [open, isEdit, recordData, allPublications, resetState]);

  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  // 保存
  const handleSave = useCallback(async () => {
    if (!form.target_taxa_id?.trim()) {
      alert("Target Taxon ID is required.");
      return;
    }
    if (!form.data_type?.trim()) {
      alert("Data Type is required.");
      return;
    }

    setSaving(true);
    try {
      const payload = toPayload(form);
      if (isEdit) {
        const { error } = await supabase.from("bionomic_records").update(payload).eq("id", form.id);
        if (error) throw error;
        alert("Bionomic record updated successfully.");
      } else {
        const { error } = await supabase.from("bionomic_records").insert([payload]);
        if (error) throw error;
        alert("Bionomic record added successfully.");
      }
      onClose?.(true);
    } catch (e) {
      console.error("[BionomicRecordEditor] save failed", e);
      alert(`Save failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  }, [form, isEdit, onClose]);

  // 削除（編集時のみ）
  const handleDelete = useCallback(async () => {
    if (!isEdit) return;
    try {
      const { error } = await supabase.from("bionomic_records").delete().eq("id", form.id);
      if (error) throw error;
      alert("Bionomic record deleted.");
      onClose?.(true);
    } catch (e) {
      console.error("[BionomicRecordEditor] delete failed", e);
      alert(`Delete failed: ${e.message || e}`);
    }
  }, [isEdit, form.id, onClose]);

  // Distribution 操作
  const openAddDistribution = () => {
    setDistEditIndex(null);
    setDistOpen(true);
  };
  const openEditDistribution = (idx) => {
    setDistEditIndex(idx);
    setDistOpen(true);
  };
  const handleCloseDistribution = (ok, row) => {
    setDistOpen(false);
    if (!ok) return;
    setForm((prev) => {
      const list = [...(prev.distribution || [])];
      if (distEditIndex == null) list.push(row);
      else list[distEditIndex] = row;
      return { ...prev, distribution: list };
    });
  };
  const handleDeleteDistribution = (idx) => {
    if (!window.confirm("Delete this distribution entry?")) return;
    setForm((prev) => ({
      ...prev,
      distribution: (prev.distribution || []).filter((_, i) => i !== idx),
    }));
  };

  // タイトル
  const title = isEdit ? `Edit Bionomic Record (ID: ${recordData?.id})` : "Add Bionomic Record";

  return (
    <>
      <BaseDialog
        open={open}
        title={title}
        onClose={onClose}
        onSubmit={handleSave}
        submitText={isEdit ? "Update" : "Add"}
        cancelText="Cancel"
        loading={saving}
        disabled={loadingOptions}
        keepMounted
        maxWidth="md"
        fullWidth
        confirmCloseWhen={isDirty}
        confirmCloseMessage="You have unsaved changes. Close without saving?"
        onDelete={isEdit ? handleDelete : undefined}
        deleteText="Delete"
      >
        {loadingOptions ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading options…</Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {/* Source & Target */}
            <Typography variant="h6" gutterBottom sx={{ color: "teal" }}>
              Source and Target Taxa
            </Typography>

            {/* Publication（IDで保持。PublicationSelectorが表示を面倒見） */}
            <PublicationSelector
              label="Source Publication"
              value={form.source_publication_id}
              onChange={(id) => handleChange("source_publication_id", id)}
              publicationsData={publications}
              onRefreshData={refreshPublications}
            />

            {/* Pages */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Page Start"
                type="number"
                value={form.page_start}
                onChange={(e) => handleChange("page_start", e.target.value)}
              />
              <TextField
                label="Page End"
                type="number"
                value={form.page_end}
                onChange={(e) => handleChange("page_end", e.target.value)}
              />
            </Stack>

            {/* Target Taxon（IDを返すセレクタ） */}
            <ScientificNameSelector
              scientificNameData={scientificNames}
              valueId={form.target_taxa_id}
              onChange={(id) => handleChange("target_taxa_id", id)}
              isRequired
              label="Target Taxon"
              onRefreshData={async () => {
                const sn = await loadScientificNamesForSelect();
                setScientificNames(sn);
              }}
            />

            <Divider sx={{ my: 2 }} />

            {/* Data Type */}
            <Typography variant="h6" gutterBottom sx={{ color: "teal" }}>
              Data Type
            </Typography>
            <FormControl component="fieldset" required>
              <FormLabel component="legend">Data Type (Required)</FormLabel>
              <RadioGroup row value={form.data_type} onChange={(e) => handleChange("data_type", e.target.value)}>
                <FormControlLabel value="distribution" control={<Radio />} label="Distribution" />
                <FormControlLabel value="ecology" control={<Radio />} label="Ecology" />
              </RadioGroup>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            {/* Distribution */}
            <Typography variant="h6" gutterBottom sx={{ color: "teal" }}>
              Distribution
            </Typography>
            <Button variant="outlined" onClick={openAddDistribution}>
              Add Distribution Data
            </Button>
            {form.distribution?.length > 0 && (
              <TableContainer sx={{ mt: 1, border: "1px solid rgba(255,255,255,0.12)" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Country</TableCell>
                      <TableCell>State</TableCell>
                      <TableCell>City</TableCell>
                      <TableCell>Detail</TableCell>
                      <TableCell>Latitude</TableCell>
                      <TableCell>Longitude</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {form.distribution.map((d, i) => (
                      <TableRow key={`${d.country}-${d.city}-${i}`} hover>
                        <TableCell>{d.country}</TableCell>
                        <TableCell>{d.state}</TableCell>
                        <TableCell>{d.city}</TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 160,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {d.detail}
                        </TableCell>
                        <TableCell>{d.latitude != null ? Number(d.latitude).toFixed(6) : ""}</TableCell>
                        <TableCell>{d.longitude != null ? Number(d.longitude).toFixed(6) : ""}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => openEditDistribution(i)}>
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteDistribution(i)}>
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Ecological Data */}
            <Typography variant="h6" gutterBottom sx={{ color: "teal" }}>
              Ecological Data
            </Typography>

            <ScientificNameSelector
              scientificNameData={scientificNames}
              valueId={form.host_taxon_id}
              onChange={(id) => handleChange("host_taxon_id", id)}
              label="Host Taxon"
              onRefreshData={async () => {
                const sn = await loadScientificNamesForSelect();
                setScientificNames(sn);
              }}
            />

            <ScientificNameSelector
              scientificNameData={scientificNames}
              valueId={form.other_related_taxon_id}
              onChange={(id) => handleChange("other_related_taxon_id", id)}
              label="Other Related Taxon"
              onRefreshData={async () => {
                const sn = await loadScientificNamesForSelect();
                setScientificNames(sn);
              }}
            />

            <Autocomplete
              multiple
              options={ecologicalTags || []}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              getOptionLabel={(o) => o?.name || ""}
              value={form.ecological_tags}
              onChange={(_, v) => handleChange("ecological_tags", v)}
              renderInput={(params) => (
                <TextField {...params} label="Ecological Tags" placeholder="Select ecological tags…" />
              )}
            />

            <Divider sx={{ my: 2 }} />

            {/* Data Origin / Reliability / Verification */}
            <Typography variant="h6" gutterBottom sx={{ color: "teal" }}>
              Data Origin and Reliability
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                select
                fullWidth
                label="Data Origin"
                value={form.data_origin_id}
                onChange={(e) => handleChange("data_origin_id", e.target.value)}
              >
                <MenuItem value="">(None)</MenuItem>
                {dataOrigins.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                label="Reliability"
                value={form.reliability_id}
                onChange={(e) => handleChange("reliability_id", e.target.value)}
              >
                <MenuItem value="">(None)</MenuItem>
                {reliabilities.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                label="Verification Status"
                value={form.verification_status_id}
                onChange={(e) => handleChange("verification_status_id", e.target.value)}
              >
                <MenuItem value="">(None)</MenuItem>
                {verificationStatuses.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* Remarks */}
            <Typography variant="h6" gutterBottom sx={{ color: "teal" }}>
              Remarks
            </Typography>
            <TextField
              label="Remark"
              value={form.remark}
              onChange={(e) => handleChange("remark", e.target.value)}
              variant="outlined"
              multiline
              rows={3}
            />
          </Stack>
        )}
      </BaseDialog>

      {/* Distribution sub-dialog（個別の小ダイアログ） */}
      <DistributionEditorDialog
        open={distOpen}
        onClose={handleCloseDistribution}
        countries={countries}
        initialValue={distEditIndex != null ? form.distribution?.[distEditIndex] : null}
      />
    </>
  );
}
